import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';

/** The smallest useful data hook: run a request, keep what it returns, and
 *  expose a way to run it again after a write. */
export function useData<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | undefined;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    load()
      .then((value) => { if (live) { setData(value); setError(null); } })
      .catch((err: unknown) => {
        if (!live) return;
        setError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
